import React from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  Link,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type {
  AddressOptionWithDays,
  SettingsConfirmationData,
  SubscriptionSettingsAnalytics,
} from '@library/api-customer-engagement-experience'

import { ADDRESS_MODAL_NAME } from './AddressModal'
import { BOX_SIZE_MODAL_NAME } from './BoxSizeModal'
import { DELIVERY_DAY_MODAL_NAME } from './DeliveryDayModal'

// Card radius and shadow.
const SettingsCardSurface = withCitrusTheme(Box, (theme) => ({
  borderRadius: '8px',
  boxShadow: `0px 2px 4px -2px ${theme.color(
    Color.ColdGrey_900,
  )}0F, 0px 4px 16px -2px ${theme.color(Color.ColdGrey_900)}0D`,
}))

const DividedRowContent = withCitrusTheme(Box, (theme) => ({
  borderBottom: `1px solid ${theme.color(Color.ColdGrey_100)}`,
}))

const SETTINGS_ROW_ICON = {
  deliveryDay: 'calendar',
  address: 'home',
  boxSize: 'serving_size',
} as const

type SettingsRowProps = {
  icon: typeof SETTINGS_ROW_ICON[keyof typeof SETTINGS_ROW_ICON]
  label: string
  editTitle: string
  onEdit: () => void
  showDivider: boolean
  children: React.ReactNode
}

const SettingsRow = ({
  icon,
  label,
  editTitle,
  onEdit,
  showDivider,
  children,
}: SettingsRowProps) => {
  const RowContent = showDivider ? DividedRowContent : Box

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      alignItems={AlignItems.FlexStart}
      gap={2}
    >
      <Icon name={icon} size={6} variant={IconVariant.Default} />
      <RowContent
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        flexGrow={1}
        gap={1}
        paddingBottom={showDivider ? 4 : 0}
      >
        <Body3 size={2} fontWeight={FontWeight.Bold} color={Color.ColdGrey_600}>
          {label}
        </Body3>
        {children}
      </RowContent>
      <Link size={3} onClick={onEdit} aria-label={`${editTitle} ${label}`}>
        {editTitle}
      </Link>
    </Box>
  )
}

type SettingsCardProps = {
  settings: SettingsConfirmationData
  dayLabel: string
  address: AddressOptionWithDays
  boxSizeLabel?: string
  onEditAction: (
    editAction: { intent: string; title: string; analytics?: SubscriptionSettingsAnalytics },
    modalName?: string,
  ) => void
}

export const SettingsCard = ({
  settings,
  dayLabel,
  address,
  boxSizeLabel,
  onEditAction,
}: SettingsCardProps) => (
  <SettingsCardSurface
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={4}
    bg={Color.White}
    paddingH={4}
    paddingV={4}
  >
    <SettingsRow
      icon={SETTINGS_ROW_ICON.deliveryDay}
      label={settings.deliveryDay.heading}
      editTitle={settings.deliveryDay.editAction.title}
      onEdit={() => onEditAction(settings.deliveryDay.editAction, DELIVERY_DAY_MODAL_NAME)}
      showDivider
    >
      <Body2 size={4} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {dayLabel}
      </Body2>
    </SettingsRow>
    <SettingsRow
      icon={SETTINGS_ROW_ICON.address}
      label={settings.address.heading}
      editTitle={settings.address.editAction.title}
      onEdit={() => onEditAction(settings.address.editAction, ADDRESS_MODAL_NAME)}
      showDivider
    >
      <Body2 size={4} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {address.name}
      </Body2>
      <Body3 size={0} color={Color.ColdGrey_800}>
        {address.label}
      </Body3>
    </SettingsRow>
    <SettingsRow
      icon={SETTINGS_ROW_ICON.boxSize}
      label={settings.boxSize.heading}
      editTitle={settings.boxSize.editAction.title}
      onEdit={() => onEditAction(settings.boxSize.editAction, BOX_SIZE_MODAL_NAME)}
      showDivider={false}
    >
      <Body2 size={4} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {boxSizeLabel}
      </Body2>
    </SettingsRow>
  </SettingsCardSurface>
)
